// # Please ensure understanding of the pricing page public setup, documented here:
// # https://product.hubteam.com/docs/growth-ssm-handbook/docs/dotcom/overview.html
// # before making changes to this file or merging in mothership changes

import { Application } from 'foundations-components/components/Application';
import { createBrowserHistory, Router } from 'quartz-routing/react-router';
import { renderRoot } from 'quartz-core/client';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import BasicPublicI18nSetup from 'I18n/init/setup/BasicPublicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import Raven from 'raven-js';
import { DataFetchingClientProvider } from 'data-fetching-client';
// Stays on the in-app client until #19825 / full Routes migration. The
// in-app client carries PRODUCTS_FIELD_POLICIES + PAGED_QUANTITY_PACK
// merge policies that the still-shared Bundle page depend on.
// Switching to the public-app's bare local client made the Bundle page
// hang waiting for a merge that never resolved.
import { dataFetchingClient } from 'pricing-pages-ui/utils/dataFetchingClient';
import Routes from 'pricing-pages-ui/Routes';
import { CurrencyContextProvider } from 'pricing-pages-ui/context/CurrencyContext';
import { PublicDerivedCohortInfoProvider } from 'pricing-pages-ui/context/DerivedCohortInfoContext';
import { ExperimentProvider } from 'pricing-pages-ui/context/ExperimentContext';
import { GlobalLoadingStateContextProvider } from 'pricing-pages-ui/context/GlobalLoadingStateContext';
import { PublicI18nContext as LegacyPublicI18nContext } from 'pricing-pages-ui/context/I18nContext';
import { CurrencyContextProvider as PublicCurrencyContextProvider } from './context/CurrencyContext';
import { PublicI18nContext } from './context/I18nContext';
import { setupMetricsFactory } from './utils/metrics';
// In-app code reachable via shared `pricing-pages-ui/Routes` reads its
// own module-scoped `Metrics`. Init that too so in-app ErrorBoundaries
// don't crash on `Metrics.counter` when something throws beneath them.
// Removed once Routes is owned by public-app (#19831).
import { setupMetricsFactory as setupInAppMetricsFactory } from 'pricing-pages-ui/utils/metrics';
import { ERROR_SEVERITY, ErrorBoundary } from './components/error-boundary/ErrorBoundary';
import { PUBLIC_APP_ROOT } from './components/error-boundary/ErrorBoundaryNames';
import { PublicAppErrorFallback } from './components/error-boundary/PublicAppErrorFallback';
// Stays on the in-app warmer until #19825. The local copy used the
// public-app's generated PublicProductRpc.dfc.new, which registers the
// query a second time under the same fieldName and the result was a
// cache miss for the still-shared Bundle page.
import { ProductCacheWarmer } from 'pricing-pages-ui/v2/components/ProductCacheWarmer';
import rhumbConfig from '../rhumb-config.yaml';
import { setupChatTracking } from './setup/setupChatTracking';
import { fetchPublicPricingPageDetails } from './setup/fetchPublicPricingPageDetails';
import { fetchUserData } from './setup/fetchUserData';
import { getManualI18nLang } from './setup/i18nGlobals';
import { prefetchCurrentRouteChunk } from './setup/prefetchCurrentRouteChunk';
import { redirectNonProxied } from './setup/redirectNonProxied';
import { setupEventTracker } from './setup/setupEventTracker';
import { setupRavenFilters } from './setup/setupRavenFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export async function start(rootUrl) {
  // Raven first so any later thrown error flows through our filters.
  setupRavenFilters();
  // Metrics factory before anything that might emit a counter, including
  // redirectNonProxied below. The in-app metrics module also needs init
  // because shared code reachable via `pricing-pages-ui/Routes` (chat
  // tracking, in-app ErrorBoundary, etc.) reads from it. Removed once
  // Routes is owned by public-app (#19831).
  setupMetricsFactory('pricing-pages-ui-public-app');
  setupInAppMetricsFactory('pricing-pages-ui-public-app');

  // Kick off i18n fetch now so it runs in parallel with the other sync
  // boot steps; we only await the result before startRouter / redirect
  // because both need i18n ready.
  const i18nManualLang = getManualI18nLang();
  const langAvailable = BasicPublicI18nSetup.setup({
    lang,
    manualLocaleSettings: i18nManualLang ? {
      locale: i18nManualLang,
      langEnabled: true
    } : undefined
  });

  // Prefetch the current route's lazy chunk alongside i18n so its network
  // and parse cost doesn't run sequentially after langAvailable.
  prefetchCurrentRouteChunk();

  // Non-critical boot steps. Chat tracking defers itself via
  // requestIdleCallback; event tracker is sync and cheap.
  setupChatTracking();
  setupEventTracker();

  // i18n is the only step in the critical path that can realistically
  // reject. Narrow the catch to just that so unrelated errors surface
  // through Raven instead of getting swallowed into an error screen.
  try {
    await langAvailable;
  } catch (error) {
    Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['failed-to-bootstrap-public-app-i18n']
    });
    startRouter(rootUrl, BasicPublicI18nSetup.provider, true);
    return;
  }

  // Must run AFTER i18n resolves: getAbsolutePricingPageUrl reads the
  // current language via getI18nLanguage() to pick the correct locale
  // host (hubspot.es, hubspot.de, etc). Running it earlier would always
  // route non-English users to www.hubspot.com.
  if (redirectNonProxied()) {
    return;
  }
  startRouter(rootUrl, BasicPublicI18nSetup.provider);
}
export function startRouter(rootUrl, i18nLoader, hasError = false) {
  if (hasError) {
    renderRoot( /*#__PURE__*/_jsx(Application, {
      name: "pricing-pages-ui-public-app",
      children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
        value: {
          i18nLoader
        },
        children: /*#__PURE__*/_jsx(LegacyPublicI18nContext.Provider, {
          value: {
            i18nLoader
          },
          children: /*#__PURE__*/_jsx(PublicAppErrorFallback, {})
        })
      })
    }));
    return;
  }
  const history = createBrowserHistory({
    basename: rootUrl
  });
  fetchUserData();
  fetchPublicPricingPageDetails();
  renderRoot( /*#__PURE__*/_jsx(Application, {
    name: "pricing-pages-ui-public-app",
    children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: dataFetchingClient,
      children: /*#__PURE__*/_jsx(ExperimentProvider, {
        children: /*#__PURE__*/_jsx(PublicI18nContext.Provider, {
          value: {
            i18nLoader
          },
          children: /*#__PURE__*/_jsx(LegacyPublicI18nContext.Provider, {
            value: {
              i18nLoader
            },
            children: /*#__PURE__*/_jsx(RhumbProvider, {
              history: historyAdapter(history),
              config: rhumbConfig,
              children: /*#__PURE__*/_jsx(GlobalLoadingStateContextProvider, {
                children: /*#__PURE__*/_jsx(CurrencyContextProvider, {
                  children: /*#__PURE__*/_jsx(Router, {
                    history: history,
                    children: /*#__PURE__*/_jsxs(ErrorBoundary, {
                      errorBoundaryName: PUBLIC_APP_ROOT,
                      severity: ERROR_SEVERITY.HIGH,
                      FallbackComponent: PublicAppErrorFallback,
                      children: [/*#__PURE__*/_jsx(ProductCacheWarmer, {}), /*#__PURE__*/_jsx(PublicDerivedCohortInfoProvider, {
                        children: /*#__PURE__*/_jsx(PublicCurrencyContextProvider, {
                          children: /*#__PURE__*/_jsx(Routes, {})
                        })
                      })]
                    })
                  })
                })
              })
            })
          })
        })
      })
    })
  }));
}